import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

export const DEFAULT_LANGUAGE = 'en';
export const SUPPORTED_LANGUAGES = ['en', 'es', 'fr'];

const resources = {
  en: {
    translation: {
      download: {
        redirecting: "Redirecting to {{store}}…"
      },
      home: {
        titleDare: "dare",
        titleAnyone: "anyone",
        titleAria: "Dare anyone",
        subtitle: "The best party game for couples and friends!",
        phonePreviewAria: "TapTrap dare card preview",
        dareLabel: "Dare",
        skip: "Skip",
        skipAria: "Skip this dare",
        refuse: "Refuse",
        refuseAria: "Refuse this dare and show penalty",
        done: "Done",
        doneAria: "Mark this dare as done",
        penalty: "Penalty",
        penaltyText: "Take a shot.",
        nextMatch: "Next match",
        getIOS: "Get it for iOS",
        getAndroid: "Get it for Android",
        copyright: "©2026 TapTrap, LLC. All rights reserved.",
        dares: [
          ["Show a photo", "from your", "Hidden album."],
          ["Kiss the", "next player."],
          ["Take off", "your clothes."],
          ["Send \"I miss u\"", "to your ex."],
          ["Send a flirty", "emoji to", "your bff."],
          ["Whisper a", "spicy secret."],
          ["Let someone", "pick your pose."],
          ["Do a slow", "dance."],
          ["Let us see your", "search history."],
          ["Post a hot", "story."]
        ]
      },
      hero: {
        titleStart: "Let's",
        titleMiddle: "dare",
        titleEnd: "anyone",
        subtitle: "Y'all put one finger on screen and let fate decide.",
        downloadIOS: "Download for iOS",
        downloadAndroid: "Download for Android"
      },
      header: {
        followUs: "Follow us",
        getTheApp: "Get the app"
      },
      footer: {
        privacy: "Privacy",
        termsOfUse: "Terms of use",
        support: "Support",
        followUs: "Follow Us",
        copyrightDesktop: "©2025 TapTrap, LLC. All rights reserved.",
        copyrightMobile: "©2025 TapTrap, LLC. All rights reserved."
      },
      links: {
        support: "https://tally.so/r/mZALq0",
        followUs: "https://www.tiktok.com/@taptrap.app"
      },
      images: {
        phoneLeft: "/assets/hero/phone-left-en.png",
        phoneRight: "/assets/hero/phone-right-en.png"
      },
      videos: {
        hero: "/assets/hero/hero-video.mp4",
        heroCover: "/assets/hero/hero-video-cover.png"
      },
      seo: {
        title: "TapTrap | Party Game",
        description: "Y'all put one finger on screen and let fate decide.",
        siteName: "TapTrap",
        url: 'https://www.taptrap.app',
        image: "/assets/op/twitter.png",
        imageAlt: "TapTrap - Party Game",
        type: "website",
        locale: "en_US",
        pages: {
          home: {
            title: "TapTrap | Party Game",
            description: "Y'all put one finger on screen and let fate decide."
          },
          terms: {
            title: "Terms & Conditions | TapTrap",
            description: "Terms and Conditions for TapTrap party game"
          },
          privacy: {
            title: "Privacy Policy | TapTrap", 
            description: "Privacy Policy for TapTrap party game"
          }
        }
      },
      404: {
        title: "Page Not Found | TapTrap",
        subtitle: "Oops! Page not found",
        description: "The page you're looking for doesn't exist.",
        message: "It looks like you got lost in the game. Don't worry, we'll help you get back on track!",
        backHome: "Back to Home"
      },
      tiktokTooltip: {
        onText: "On",
        needToTap: "hit the 3-dots menu",
        andThenTap: ". Then tap",
        openInBrowser: "Open in browser",
        toDownload: "to download the app for free."
      },
      howToPlay: {
        intro: "TapTrap is easy to play. Watch the video below or follow the instructions to make the most of the game:",
        sections: {
          gettingStarted: {
            title: "Getting Started",
            text: "Grab a friend and each of you has to place one finger on the screen. A 3-second countdown begins, then one finger is randomly selected. That player must answer the question or complete the dare!"
          },
          skipDares: {
            title: "Skip Dares",
            text: "Not feeling the current challenge? Hit the skip button to get a new question or dare. Keep the game moving at your own pace."
          },
          penalties: {
            title: "Penalties",
            text: "Don't want to do a dare or answer a question? Hit \"Refuse\" and face a penalty instead! You can customize penalties in settings."
          },
          create: {
            title: "Create",
            text: "You create custom categories and dares to personalize your game experience. Tap on \"Create\" in the \"Pick a level\" screen and follow the instructions."
          }
        }
      },
      deleteAccount: {
        title: "Delete Historical Account Data",
        subtitle: "The current TapTrap app does not create an account. This process is only for accounts created by an older version of TapTrap.",
        localDataTitle: "Current app data stays on your device",
        localDataText: "Delete the app or clear its storage to remove onboarding data, preferences, downloaded content, and locally encrypted custom categories. TapTrap cannot remotely delete or recover that local data.",
        howToTitle: "How do I delete an older account?",
        howToIntro: "Support must verify that you own the historical account before deleting it. Follow these steps:",
        step1: "Send an email to hi@taptrap.app from the email connected to the older account. Include any provider ID, RevenueCat ID, or TapTrap user ID you still have:",
        emailFormatLabel: "Use this email format:",
        emailFormatText: "Hi, I want to delete my historical TapTrap account and its data. Account email: [email]. Known ID, if available: [provider, RevenueCat, or user ID].",
        step2: "Support will verify ownership and tell you when the request has been verified or if more information is needed.",
        step3: "After verification, TapTrap will delete the historical account, cloud-created content, game progress, and stored devices within 30 days and send you a confirmation. Ask for a copy of your information in the same email if you want one before deletion.",
        seo: {
          title: "Delete Historical Account Data | TapTrap",
          description: "Request verified deletion of an account created with an older version of TapTrap."
        }
      }
    }
  },
  es: {
    translation: {
      download: {
        redirecting: "Redirigiendo a {{store}}…"
      },
      home: {
        titleDare: "Reta",
        titleAnyone: "a todos",
        titleAria: "Reta a todos",
        subtitle: "¡El mejor juego para parejas y amigos!",
        phonePreviewAria: "Vista previa de una carta de reto de TapTrap",
        dareLabel: "Reto",
        skip: "Saltar",
        skipAria: "Saltar este reto",
        refuse: "Negarse",
        refuseAria: "Negarse a este reto y mostrar el castigo",
        done: "Hecho",
        doneAria: "Marcar este reto como hecho",
        penalty: "Castigo",
        penaltyText: "Toma un shot.",
        nextMatch: "Siguiente",
        getIOS: "Descargar para iOS",
        getAndroid: "Descargar para Android",
        copyright: "©2026 TapTrap, LLC. Todos los derechos reservados.",
        dares: [
          ["Muestra una foto", "de tus ocultos."],
          ["Besa al", "siguiente jugador."],
          ["Quitate", "la ropa."],
          ["Envia \"te extrano\"", "a tu ex."],
          ["Envia un emoji", "coqueto a", "tu bff."],
          ["Susurra un", "secreto picante."],
          ["Deja que alguien", "elija tu pose."],
          ["Haz un baile", "lento."],
          ["Dejanos ver tu", "historial de", "busqueda."],
          ["Sube una historia", "hot."]
        ]
      },
      hero: {
        titleStart: "Reta",
        titleMiddle: "a tus",
        titleEnd: "amigos",
        subtitle: "Todos pongan un dedo en la pantalla y dejen que el destino decida.",
        downloadIOS: "Descargar para iOS",
        downloadAndroid: "Descargar para Android"
      },
      header: {
        followUs: "Síguenos",
        getTheApp: "Obtén la app"
      },
      footer: {
        privacy: "Privacidad",
        termsOfUse: "Términos de uso",
        support: "Soporte",
        followUs: "Síguenos",
        copyrightDesktop: "©2025 TapTrap, tato'. Todos los derechos reservados.",
        copyrightMobile: "©2025 TapTrap. Todos los derechos reservados."
      },
      links: {
        support: "https://tally.so/r/mRQ6Od",
        followUs: "https://www.tiktok.com/@taptrap.game"
      },
      images: {
        phoneLeft: "/assets/hero/phone-left-es.png",
        phoneRight: "/assets/hero/phone-right-es.png"
      },
      videos: {
        hero: "/assets/hero/hero-video-es.mp4",
        heroCover: "/assets/hero/hero-video-cover-es.png"
      },
      seo: {
        title: "TapTrap | Juego de fiestas",
        description: "Todos pongan un dedo en la pantalla y dejen que el destino decida.",
        siteName: "TapTrap",
        url: 'https://www.taptrap.app',
        image: "/assets/op/twitter.png",
        imageAlt: "TapTrap - Juego de fiestas",
        type: "website",
        locale: "es_ES",
        pages: {
          home: {
            title: "TapTrap | Juego de fiestas",
            description: "Todos pongan un dedo en la pantalla y dejen que el destino decida."
          },
          terms: {
            title: "Términos y Condiciones | TapTrap",
            description: "Términos y Condiciones para el juego de fiestas TapTrap"
          },
          privacy: {
            title: "Política de Privacidad | TapTrap",
            description: "Política de Privacidad para el juego de fiestas TapTrap"
          }
        }
      },
      404: {
        title: "Página no encontrada | TapTrap",
        subtitle: "¡Ups! Página no encontrada",
        description: "La página que buscas no existe.",
        message: "Parece que te perdiste en el juego. ¡No te preocupes, te ayudaremos a volver al camino!",
        backHome: "Volver al inicio"
      },
      tiktokTooltip: {
        onText: "En",
        needToTap: "necesitas tocar en",
        andThenTap: "y luego tocar en",
        openInBrowser: "Abrir en navegador",
        toDownload: "para descargar la app gratis."
      },
      howToPlay: {
        intro: "TapTrap es fácil de jugar. Mira el video a continuación o sigue las instrucciones para aprovechar al máximo el juego:",
        sections: {
          gettingStarted: {
            title: "Comenzar",
            text: "Busca un amigo y cada uno debe colocar un dedo en la pantalla. Comienza una cuenta regresiva de 3 segundos, luego se selecciona un dedo al azar. ¡Ese jugador debe responder la pregunta o completar el reto!"
          },
          skipDares: {
            title: "Saltar Retos",
            text: "¿No te gusta el desafío actual? Presiona el botón de saltar para obtener una nueva pregunta o reto. Mantén el juego en movimiento a tu propio ritmo."
          },
          penalties: {
            title: "Penalizaciones",
            text: "¿No quieres hacer un reto o responder una pregunta? ¡Presiona \"Me niego\" y enfrenta una penalización en su lugar! Puedes personalizar las penalizaciones en la configuración."
          },
          create: {
            title: "Crear",
            text: "Puedes crear categorías y retos personalizados para personalizar tu experiencia de juego. Toca \"Crear\" en la pantalla \"Elige un nivel\" y sigue las instrucciones."
          }
        }
      },
      deleteAccount: {
        title: "Eliminar Datos de una Cuenta Histórica",
        subtitle: "La app actual de TapTrap no crea una cuenta. Este proceso es solo para cuentas creadas por una versión anterior de TapTrap.",
        localDataTitle: "Los datos de la app actual permanecen en tu dispositivo",
        localDataText: "Elimina la app o borra su almacenamiento para quitar el onboarding, las preferencias, el contenido descargado y las categorías personalizadas cifradas localmente. TapTrap no puede eliminar ni recuperar esos datos de forma remota.",
        howToTitle: "¿Cómo elimino una cuenta anterior?",
        howToIntro: "Soporte debe verificar que eres dueño de la cuenta histórica antes de eliminarla. Sigue estos pasos:",
        step1: "Envía un correo a hi@taptrap.app desde el email vinculado a la cuenta anterior. Incluye cualquier ID del proveedor, RevenueCat o usuario de TapTrap que todavía tengas:",
        emailFormatLabel: "Usa este formato de correo:",
        emailFormatText: "Hola, quiero eliminar mi cuenta histórica de TapTrap y sus datos. Email de la cuenta: [email]. ID conocido, si está disponible: [proveedor, RevenueCat o ID de usuario].",
        step2: "Soporte verificará que la cuenta es tuya y te informará cuando la solicitud esté verificada o si necesita más información.",
        step3: "Después de verificarla, TapTrap eliminará la cuenta histórica, el contenido creado en la nube, el progreso del juego y los dispositivos guardados en un plazo de 30 días, y te enviará una confirmación. Si quieres una copia de tu información antes de eliminarla, solicítala en el mismo correo.",
        seo: {
          title: "Eliminar Datos de una Cuenta Histórica | TapTrap",
          description: "Solicita la eliminación verificada de una cuenta creada con una versión anterior de TapTrap."
        }
      }
    }
  },
  fr: {
    translation: {
      download: {
        redirecting: "Redirection vers {{store}}…"
      },
      home: {
        titleDare: "Défie",
        titleAnyone: "tes amis",
        titleAria: "Défie tes amis",
        subtitle: "Le meilleur jeu de soirée pour couples et amis !",
        phonePreviewAria: "Aperçu d'une carte de défi TapTrap",
        dareLabel: "Défi",
        skip: "Passer",
        skipAria: "Passer ce défi",
        refuse: "Refuser",
        refuseAria: "Refuser ce défi et afficher le gage",
        done: "Fait",
        doneAria: "Marquer ce défi comme fait",
        penalty: "Gage",
        penaltyText: "Prends un shot.",
        nextMatch: "Suivant",
        getIOS: "Pour iOS",
        getAndroid: "Pour Android",
        copyright: "©2026 TapTrap, LLC. Tous droits réservés.",
        dares: [
          ["Montre une photo", "de ton album", "masqué."],
          ["Embrasse le", "joueur suivant."],
          ["Enlève", "tes vêtements."],
          ["Envoie \"tu me manques\"", "à ton ex."],
          ["Envoie un emoji", "coquin à", "ton/ta BFF."],
          ["Murmure un", "secret épicé."],
          ["Laisse quelqu'un", "choisir ta pose."],
          ["Fais une danse", "lente."],
          ["Montre-nous ton", "historique de", "recherche."],
          ["Poste une story", "hot."]
        ]
      },
      hero: {
        titleStart: "Défie",
        titleMiddle: "tes",
        titleEnd: "amis",
        subtitle: "Posez tous un doigt sur l'écran et laissez le destin décider.",
        downloadIOS: "Pour iOS",
        downloadAndroid: "Pour Android"
      },
      header: {
        followUs: "Suivez-nous",
        getTheApp: "Télécharger"
      },
      footer: {
        privacy: "Confidentialité",
        termsOfUse: "Conditions d'utilisation",
        support: "Assistance",
        followUs: "Suivez-nous",
        copyrightDesktop: "©2025 TapTrap, LLC. Tous droits réservés.",
        copyrightMobile: "©2025 TapTrap, LLC. Tous droits réservés."
      },
      links: {
        support: "https://tally.so/r/mZALq0",
        followUs: "https://www.tiktok.com/@taptrap.app"
      },
      images: {
        phoneLeft: "/assets/hero/phone-left-en.png",
        phoneRight: "/assets/hero/phone-right-en.png"
      },
      videos: {
        hero: "/assets/hero/hero-video.mp4",
        heroCover: "/assets/hero/hero-video-cover.png"
      },
      seo: {
        title: "TapTrap | Jeu de soirée",
        description: "Posez tous un doigt sur l'écran et laissez le destin décider.",
        siteName: "TapTrap",
        url: 'https://www.taptrap.app',
        image: "/assets/op/twitter.png",
        imageAlt: "TapTrap - Jeu de soirée",
        type: "website",
        locale: "fr_FR",
        pages: {
          home: {
            title: "TapTrap | Jeu de soirée",
            description: "Posez tous un doigt sur l'écran et laissez le destin décider."
          },
          terms: {
            title: "Conditions générales | TapTrap",
            description: "Conditions générales du jeu de soirée TapTrap"
          },
          privacy: {
            title: "Politique de confidentialité | TapTrap",
            description: "Politique de confidentialité du jeu de soirée TapTrap"
          }
        }
      },
      404: {
        title: "Page introuvable | TapTrap",
        subtitle: "Oups ! Page introuvable",
        description: "La page que vous cherchez n'existe pas.",
        message: "On dirait que vous vous êtes perdu dans le jeu. Pas d'inquiétude, on vous remet sur la bonne piste !",
        backHome: "Retour à l'accueil"
      },
      tiktokTooltip: {
        onText: "Sur",
        needToTap: "appuyez sur le menu à 3 points",
        andThenTap: ". Puis appuyez sur",
        openInBrowser: "Ouvrir dans le navigateur",
        toDownload: "pour télécharger l'app gratuitement."
      },
      howToPlay: {
        intro: "TapTrap est facile à jouer. Regardez la vidéo ci-dessous ou suivez les instructions pour profiter au maximum du jeu :",
        sections: {
          gettingStarted: {
            title: "Commencer",
            text: "Choisissez un ami et posez chacun un doigt sur l'écran. Un compte à rebours de 3 secondes commence, puis un doigt est choisi au hasard. Ce joueur doit répondre à la question ou relever le défi !"
          },
          skipDares: {
            title: "Passer des défis",
            text: "Le défi ne vous tente pas ? Appuyez sur le bouton passer pour obtenir une nouvelle question ou un nouveau défi. Gardez le rythme qui vous convient."
          },
          penalties: {
            title: "Gages",
            text: "Vous ne voulez pas relever un défi ou répondre à une question ? Appuyez sur \"Refuser\" et recevez un gage à la place ! Vous pouvez personnaliser les gages dans les réglages."
          },
          create: {
            title: "Créer",
            text: "Vous pouvez créer des catégories et des défis personnalisés pour adapter l'expérience de jeu. Appuyez sur \"Créer\" dans l'écran \"Choisir un niveau\" et suivez les instructions."
          }
        }
      },
      deleteAccount: {
        title: "Supprimer les données d'un ancien compte",
        subtitle: "L'application TapTrap actuelle ne crée pas de compte. Cette procédure concerne uniquement les comptes créés avec une ancienne version de TapTrap.",
        localDataTitle: "Les données actuelles restent sur votre appareil",
        localDataText: "Supprimez l'application ou effacez son stockage pour retirer l'onboarding, les préférences, le contenu téléchargé et les catégories personnalisées chiffrées localement. TapTrap ne peut ni supprimer ni récupérer ces données à distance.",
        howToTitle: "Comment supprimer un ancien compte ?",
        howToIntro: "L'assistance doit vérifier que l'ancien compte vous appartient avant de le supprimer. Suivez ces étapes :",
        step1: "Envoyez un e-mail à hi@taptrap.app depuis l'adresse associée à l'ancien compte. Ajoutez tout identifiant de fournisseur, RevenueCat ou utilisateur TapTrap dont vous disposez encore :",
        emailFormatLabel: "Utilisez ce format d'e-mail :",
        emailFormatText: "Bonjour, je souhaite supprimer mon ancien compte TapTrap et ses données. E-mail du compte : [e-mail]. Identifiant connu, si disponible : [fournisseur, RevenueCat ou ID utilisateur].",
        step2: "L'assistance vérifiera que le compte vous appartient et vous indiquera quand la demande est vérifiée ou si des informations supplémentaires sont nécessaires.",
        step3: "Après vérification, TapTrap supprimera l'ancien compte, le contenu créé dans le cloud, la progression et les appareils enregistrés sous 30 jours, puis vous enverra une confirmation. Demandez une copie de vos informations dans le même e-mail si vous la souhaitez avant la suppression.",
        seo: {
          title: "Supprimer les données d'un ancien compte | TapTrap",
          description: "Demandez la suppression vérifiée d'un compte créé avec une ancienne version de TapTrap."
        }
      }
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES,
    nonExplicitSupportedLngs: true,
    debug: false,
    interpolation: {
      escapeValue: false
    },
    detection: {
      order: ['querystring', 'navigator', 'localStorage', 'htmlTag'],
      lookupQuerystring: 'lang',
      caches: ['localStorage']
    }
  });

export default i18n;
